import React, { useState, useEffect } from "react";
import "../css/css_pages/Dashboard.css";
import "../css/css_pages/comments-modal.css";
import "../css/css_pages/UpdateTicketInfo.css";
import Cookies from "js-cookie";
import {
  FiImage,
  FiMessageSquare,
  FiSend,
  FiUpload,
  FiUser,
  FiEdit,
  FiPhone,
  FiSave,
  FiX
} from "react-icons/fi";
import config from "../../config";
import { useTranslation } from 'react-i18next';

const Tasks = () => {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [selectedFile, setSelectedFile] = useState({ name: "", type: "" });
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [loadingComments, setLoadingComments] = useState({});
  const [commentErrors, setCommentErrors] = useState({});
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState({});
  const [uploadingResource, setUploadingResource] = useState(false);
  const [resourceFile, setResourceFile] = useState(null);
  const [resourceUploadSuccess, setResourceUploadSuccess] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedTicketForUpdate, setSelectedTicketForUpdate] = useState(null);

  useEffect(() => {
    const authToken = Cookies.get("authToken");
    if (authToken) {
      setToken(authToken);
    }
  }, []);

  useEffect(() => {
    if (token) {
      console.log("Updated token:", token);
      filterIssues("today");
    }
  }, [token]);


  const fetchComments = async (ticketId) => {
  try {
    setCommentErrors((prev) => ({ ...prev, [ticketId]: null }));
    const token = Cookies.get("authToken");
    if (!token) {
      throw new Error(t("auth_error"));
    }

    console.log("Token being used:", token); // Log the token to verify it's correct

    const response = await fetch(
      `${config.API_URL}/tso-online/get-all-ticket-comments/${ticketId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Error response from server:", errorData);
      throw new Error(errorData.message || t("fetch_comments_error"));
    }

    const data = await response.json();
    return Array.isArray(data.response) ? data.response : [];
  } catch (error) {
    console.error("Error fetching comments:", error);
    setCommentErrors((prev) => ({
      ...prev,
      [ticketId]: error.message || t("fetch_comments_error"),
    }));
    return [];
  }
};


  const addComment = async (ticketId) => {
    if (!newComment.trim()) {
        setCommentErrors(prev => ({ ...prev, [ticketId]: "Comment cannot be empty" }));
        return;
    }

    setSendingComment(true);
    setCommentErrors(prev => ({ ...prev, [ticketId]: null }));
    setCommentSuccess(prev => ({ ...prev, [ticketId]: null }));

    try {
        const token = Cookies.get("authToken");
        if (!token) {
            throw new Error(t('auth_error'));
        }

        const response = await fetch(
            `${config.API_URL}/client/add-ticket-comment`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ticket_id: ticketId,
                    comment: newComment,
                }),
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || t('comment_add_error'));
        }

        // Update comments list immediately with the new comment
        const newCommentObj = {
            id: result.id,
            comment: result.comment,
            commented_by: result.added_by,
            commented_date: result.commented_date
        };

        setComments(prev => ({
            ...prev,
            [ticketId]: [...(prev[ticketId] || []), newCommentObj]
        }));

        setNewComment("");
        setCommentSuccess(prev => ({ ...prev, [ticketId]: t('comment_add_success') }));

        setTimeout(() => {
            setCommentSuccess(prev => ({ ...prev, [ticketId]: null }));
        }, 3000);
    } catch (error) {
        console.error('Error adding comment:', error);
        setCommentErrors(prev => ({
            ...prev,
            [ticketId]: error.message || t('comment_add_error')
        }));
    } finally {
        setSendingComment(false);
    }
};

  const uploadResource = async (ticketId) => {
    if (!resourceFile) return;

    setUploadingResource(true);
    setCommentErrors(prev => ({ ...prev, [ticketId]: null }));
    setResourceUploadSuccess(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t('auth_error'));
      }

      const formData = new FormData();
      formData.append("file", resourceFile);
      formData.append("ticket_id", ticketId);
      formData.append("append", "true"); // Add flag to append rather than replace

      const response = await fetch(
        `${config.API_URL}/client/upload-resource`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t('resource_upload_error'));
      }

      setResourceUploadSuccess(t('resource_upload_success'));
      setTimeout(() => setResourceUploadSuccess(null), 3000);
      
      // Refresh the issues list to show the new resource
      filterIssues("today");
    } catch (error) {
      console.error('Error uploading resource:', error);
      setCommentErrors(prev => ({ 
        ...prev, 
        [ticketId]: error.message || t('resource_upload_error')
      }));
    } finally {
      setUploadingResource(false);
      setResourceFile(null);
    }
  };

  const updateTicketInfo = async (ticketId, phoneNumber, files) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t('auth_error'));
      }

      const formData = new FormData();
      if (phoneNumber) {
        formData.append("contact_person_phone_number", phoneNumber);
      }
      if (files) {
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i]);
        }
      }

      const response = await fetch(
        `${config.API_URL}/client/update-files-and-contact/${ticketId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t('update_error'));
      }

      return true;
    } catch (error) {
      console.error('Update error:', error);
      throw error;
    }
  };

  const fetchIssues = async (startDate, endDate) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t('auth_error'));
      }

      const response = await fetch(
        `${config.API_URL}/client/get-all-tickets-by-customer-id?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('fetch_error'));
      }

      const data = await response.json();
      console.log("Issues", data)
      if (!data || !Array.isArray(data)) {
        throw new Error(t('invalid_data'));
      }

      setFilteredIssues(data);

      const commentsData = {};
      for (const issue of data) {
        try {
          const issueComments = await fetchComments(issue.ticket_id);
          commentsData[issue.ticket_id] = issueComments;
        } catch (error) {
          commentsData[issue.ticket_id] = [];
          console.error(`Error fetching comments for ticket ${issue.ticket_id}:`, error);
        }
      }
      setComments(commentsData);
    } catch (error) {
      console.error('Error fetching issues:', error);
      setFilteredIssues([]);
    }
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    };

    if (!showOnlyTime) {
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
    }

    return date.toLocaleString('en-US', options).replace(',', '');
  };

  const filterIssues = (filterType) => {
    const currentDate = new Date();
    let startDate, endDate;

    switch (filterType) {
      case "today":
        startDate = endDate = currentDate.toISOString().split("T")[0];
        break;
      case "thisWeek":
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = startOfWeek.toISOString().split("T")[0];
        endDate = endOfWeek.toISOString().split("T")[0];
        break;
      case "thisMonth":
        const startOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        startDate = startOfMonth.toISOString().split("T")[0];
        endDate = endOfMonth.toISOString().split("T")[0];
        break;
      case "thisYear":
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
        startDate = startOfYear.toISOString().split("T")[0];
        endDate = endOfYear.toISOString().split("T")[0];
        break;
      default:
        startDate = endDate = currentDate.toISOString().split("T")[0];
    }

    fetchIssues(startDate, endDate);
  };

  const openResource = async (resource, issue) => {
    setSelectedIssue(issue);

    const url = `${config.API_URL}/client/view-image?ticket_id=${issue.ticket_id}&file_name=${resource}`;
    const token = Cookies.get("authToken");
    if (!token) {
      throw new Error(t('auth_error'));
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const fileType = blob.type;

        setSelectedFile({ name: resource, type: fileType });

        if (fileType.startsWith("image/")) {
          const imageURL = URL.createObjectURL(blob);
          setImageSrc(imageURL);
        } else {
          const fileURL = URL.createObjectURL(blob);
          window.open(fileURL, "_blank");
        }
      } else {
        const errorData = await response.json();
        console.error(t('resource_fetch_error'), errorData.message);
      }
    } catch (error) {
      console.error(t('resource_error_details'), error);
    }
  };

  const toggleComments = async (ticketId) => {
    setLoadingComments(prev => ({ ...prev, [ticketId]: true }));

    try {
      if (currentTicketId === ticketId) {
        setShowComments(!showComments);
      } else {
        const fetchedComments = await fetchComments(ticketId);
        setComments(prev => ({ ...prev, [ticketId]: fetchedComments }));
        setCurrentTicketId(ticketId);
        setShowComments(true);
      }
    } catch (error) {
      console.error('Error toggling comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const closeImageModal = () => {
    setImageSrc(null);
    setSelectedIssue(null);
    setSelectedFile({ name: "", type: "" });
  };

  const closeCommentsModal = () => {
    setShowComments(false);
    setCurrentTicketId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return { backgroundColor: "#faafaf", color: "#2f0909" };
      case "in_progress":
        return { backgroundColor: "#fff3cd", color: "#856404" };
      case "completed":
        return { backgroundColor: "#d4edda", color: "#155724" };
      case "closed":
        return { backgroundColor: "#d6d8d9", color: "#1b1e21" };
      default:
        return { backgroundColor: "#f8f9fa", color: "#212529" };
    }
  };

  const formatStatusText = (status) => {
    const statusMap = {
      pending: t('status_pending'),
      in_progress: t('status_in_progress'),
      completed: t('status_completed'),
      closed: t('status_closed')
    };
    return statusMap[status] || status;
  };

  const UpdateTicketModal = ({ ticketId, currentPhone, onClose, onUpdateSuccess }) => {
    const [phoneNumber, setPhoneNumber] = useState(currentPhone || "");
    const [files, setFiles] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleFileChange = (e) => {
      setFiles(e.target.files);
    };

    const handleSubmit = async () => {
      if (!phoneNumber && !files) {
        setError(t('provide_phone_or_file'));
        return;
      }

      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      try {
        const updated = await updateTicketInfo(ticketId, phoneNumber, files);
        if (updated) {
          setSuccess(t('update_success'));
          setTimeout(() => {
            onUpdateSuccess();
          }, 1500);
        }
      } catch (err) {
        setError(err.message || t('update_error'));
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="update-ticket-modal">
        <div className="update-ticket-content">
          <div className="update-ticket-header">
            <h3>{t('update_ticket_info')}</h3>
            <button onClick={onClose} className="close-button">
              <FiX size={20} />
            </button>
          </div>

          <div className="update-ticket-form">
            <div className="form-group">
              <label>
                <FiPhone className="input-icon" />
                {t('contact_phone_number')}
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t('enter_phone_number')}
              />
            </div>

            <div className="form-group">
              <label>
                <FiUpload className="input-icon" />
                {t('upload_files')}
              </label>
              <div className="file-upload-wrapper">
                <label className="file-upload-label">
                  {files 
                    ? `${files.length} ${t('files_selected')}` 
                    : t('choose_files')}
                  <input
                    type="file"
                    onChange={handleFileChange}
                    multiple
                    className="file-upload-input"
                  />
                </label>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-actions">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="submit-button"
              >
                <FiSave className="button-icon" />
                {isSubmitting ? t('updating') : t('update')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <h1>{t('tasks')}</h1>

      <div className="filter-buttons">
        <button onClick={() => filterIssues("today")}>{t('today')}</button>
        <button onClick={() => filterIssues("thisWeek")}>{t('this_week')}</button>
        <button onClick={() => filterIssues("thisMonth")}>{t('this_month')}</button>
        <button onClick={() => filterIssues("thisYear")}>{t('this_year')}</button>
      </div>

      {filteredIssues.length > 0 ? (
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>{t('id')}</th>
              <th>{t('issue')}</th>
              <th>{t('Contact_Phone')}</th>
              <th>{t('resources')}</th>
              <th>{t('date_time')}</th>
              <th>{t('status')}</th>
              <th>{t('comments')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {[...filteredIssues]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map((issue) => (
                <tr key={issue.ticket_id}>
                  <td>{issue.ticket_no}</td>
                  <td style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                    {issue.issue}
                  </td>
                  <td>{issue.contact_person_phone_number || t("No_phone_number_provided")}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {issue.filename ? (
                        issue.filename.split(",").map((resource, index) => (
                          <button
                            key={index}
                            className="resource-link"
                            onClick={() => openResource(resource, issue)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "4px",
                            }}
                          >
                            <FiImage size={20} color="#1C3663" />
                          </button>
                        ))
                      ) : (
                        <span style={{ marginRight: "8px" }}>{t('no_resources')}</span>
                      )}
                      
                    </div>
                  </td>
                  <td>
                    <input
                      value={formatDateTime(issue.created_at)}
                      readOnly
                      style={{
                        border: "none",
                        backgroundColor: "#cce5ff",
                        color: "black",
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        width: "65%",
                        cursor: "default",
                        borderRadius: "100px",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      ...getStatusColor(issue.ticket_status),
                      padding: "5px",
                      textAlign: "center",
                    }}
                  >
                    {formatStatusText(issue.ticket_status)}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleComments(issue.ticket_id)}
                      className="comment-button"
                      title={t('view_comments')}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        position: "relative",
                      }}
                    >
                      <FiMessageSquare size={20} color="#1C3663" />
                      {comments[issue.ticket_id]?.length > 0 && (
                        <span className="comment-count-badge">
                          {comments[issue.ticket_id].length}
                        </span>
                      )}
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        setSelectedTicketForUpdate(issue);
                        setShowUpdateModal(true);
                      }}
                      className="update-button"
                      title={t('update_ticket_info')}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                      }}
                    >
                      <FiEdit size={18} color="#1C3663" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      ) : (
        <p>{t('no_issues_found')}</p>
      )}

      {imageSrc && (
        <div className="modal">
          <div className="modalContent">
            <span className="close" onClick={closeImageModal}>
              &times;
            </span>
            <h3>{t('file_details')}</h3>
            <p>{t('name')}: {selectedFile.name}</p>
            <p>{t('type')}: {selectedFile.type}</p>
            <img
              src={imageSrc}
              alt={t('selected_file')}
              style={{ maxWidth: "100%" }}
            />
          </div>
        </div>
      )}

      {showComments && currentTicketId && (
        <div className="modal">
          <div className="modalContent comments-modal">
            <span className="close" onClick={closeCommentsModal}>
              &times;
            </span>
            <h3>
              {t('comments_for_ticket')} - 
              {filteredIssues.find(issue => issue.ticket_id === currentTicketId)?.ticket_no}
            </h3>
            {loadingComments[currentTicketId] ? (
              <p>{t('loading_comments')}</p>
            ) : commentErrors[currentTicketId] ? (
              <p className="error-text">{commentErrors[currentTicketId]}</p>
            ) : (
              <>
                <div className="comments-list">
                  {(comments[currentTicketId] || []).length > 0 ? (
                    comments[currentTicketId].map((comment, index) => (
                      <div key={index} className="comment-item">
                        <div className="comment-meta">
                          <span className="comment-author">
                            <FiUser size={14} />
                            {comment.commented_by}
                          </span>
                          <span className="comment-date">
                            {formatDateTime(comment.commented_date)}
                          </span>
                        </div>
                        <p className="comment-text">{comment.comment}</p>
                      </div>
                    ))
                  ) : (
                    <p>{t('no_comments_found')}</p>
                  )}
                </div>

                <div className="add-comment-section">
                  {commentSuccess[currentTicketId] && (
                    <div className="comment-success">{commentSuccess[currentTicketId]}</div>
                  )}
                  {commentErrors[currentTicketId] && (
                    <div className="error-text">{commentErrors[currentTicketId]}</div>
                  )}
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('write_your_comment')}
                    rows="3"
                    disabled={sendingComment}
                  />
                  <button
                    onClick={() => addComment(currentTicketId)}
                    disabled={sendingComment || !newComment.trim()}
                    className="send-comment-button"
                  >
                    {sendingComment ? t('sending') : t('send')}
                    <FiSend className="send-icon" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {resourceFile && currentTicketId && (
        <div className="modal">
          <div className="modalContent">
            <span className="close" onClick={() => setResourceFile(null)}>
              &times;
            </span>
            <h3>{t('upload_resource')}</h3>
            <p>{t('selected_file')}: {resourceFile.name}</p>
            {resourceUploadSuccess && (
              <div className="success-message">{resourceUploadSuccess}</div>
            )}
            {commentErrors[currentTicketId] && (
              <div className="error-text">{commentErrors[currentTicketId]}</div>
            )}
            <button
              onClick={() => uploadResource(currentTicketId)}
              disabled={uploadingResource}
              className="upload-button"
            >
              {uploadingResource ? t('uploading') : t('upload')}
            </button>
          </div>
        </div>
      )}

      {showUpdateModal && selectedTicketForUpdate && (
        <UpdateTicketModal
          ticketId={selectedTicketForUpdate.ticket_id}
          currentPhone={selectedTicketForUpdate.contact_person_phone_number}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedTicketForUpdate(null);
          }}
          onUpdateSuccess={() => {
            filterIssues("today");
            setShowUpdateModal(false);
            setSelectedTicketForUpdate(null);
          }}
        />
      )}
    </div>
  );
};

export default Tasks;